import React, { useState } from "react";
import api, { formatTL, formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveQuery, TAGS, invalidate } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bank, Money, CreditCard, ArrowRight, Trash, Wallet, Receipt, Plus, FunnelSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BankTransfers() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [tab, setTab] = useState("transfers");
  const [dialog, setDialog] = useState({ open: false, method: "all", note: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);

  const { data: pending } = useLiveQuery(
    TAGS.BANK_TRANSFERS,
    async () => (await api.get("/bank-transfers/pending")).data,
    { intervalMs: 15000 },
  );
  const { data: balance } = useLiveQuery(
    [TAGS.BANK_TRANSFERS],
    async () => (await api.get("/bank-transfers/balance")).data,
    { intervalMs: 15000 },
  );
  const { data: history } = useLiveQuery(
    TAGS.BANK_TRANSFERS,
    async () => (await api.get("/bank-transfers")).data,
    { intervalMs: 30000 },
  );

  const openDialog = (method) => setDialog({ open: true, method, note: "", amount: "" });

  const submit = async () => {
    // Kısmi aktarım tutarı doğrulama (all için desteklenmez)
    let amount = null;
    if (dialog.method !== "all" && dialog.amount !== "") {
      const parsed = Number(dialog.amount);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        toast.error("Geçerli bir tutar girin");
        return;
      }
      const pendingAmt = dialog.method === "cash" ? p.cash.amount : p.card.amount;
      if (parsed > pendingAmt + 0.005) {
        toast.error(`Tutar elde bekleyenden (${formatTL(pendingAmt)}) büyük olamaz`);
        return;
      }
      amount = parsed;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bank-transfers/transfer", {
        method: dialog.method, note: dialog.note || null,
        ...(amount !== null ? { amount } : {}),
      });
      const parts = (data.transferred || []).map((t) => {
        const label = t.method === "cash" ? "Nakit" : "Kart";
        const tag = t.link_type === "manual_amount" ? " (kısmi)" : "";
        return `${label}${tag}: ${formatTL(t.amount)}${t.count ? ` (${t.count})` : ""}`;
      });
      toast.success("Bankaya aktarıldı → " + parts.join(" · "));
      setDialog({ open: false, method: "all", note: "", amount: "" });
      invalidate([TAGS.BANK_TRANSFERS, TAGS.KASA]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktarım başarısız");
    } finally { setSubmitting(false); }
  };

  const revert = async (id) => {
    if (!window.confirm("Bu aktarım geri alınsın mı? İlgili ödemeler yeniden 'elde bekleyen' olarak sayılacak.")) return;
    try {
      await api.delete(`/bank-transfers/${id}`);
      toast.success("Aktarım geri alındı");
      invalidate([TAGS.BANK_TRANSFERS, TAGS.KASA]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Geri alınamadı"); }
  };

  const p = pending || { cash: { amount: 0, count: 0 }, card: { amount: 0, count: 0 }, total: 0 };
  const bal = balance?.balance || 0;
  const items = history || [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Kasa</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Banka Aktarımları & Giderler</h1>
        <div className="text-sm text-muted-foreground mt-1">
          Kasa&apos;daki nakit/kart tahsilatlarını banka hesabına aktarın veya işletme giderlerini (SGK, maaş, kira, elektrik...) kaydedin — seçilen ödeme yönteminden otomatik düşülür.
        </div>
      </div>

      {/* BALANCE HERO */}
      <Card className="rounded-sm border-primary/40 bg-primary/5">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Banka Hesabı Bakiyesi</div>
            <div className="text-5xl font-bold font-mono text-primary mt-2" data-testid="bank-balance-total">
              {formatTL(bal)}
            </div>
            <div className="text-xs text-muted-foreground mt-2 font-mono">
              EFT/Havale (otopark) {formatTL(balance?.eft_parking || 0)} +
              EFT/Havale (abonelik) {formatTL(balance?.eft_subscription || 0)} +
              Manuel aktarımlar {formatTL(balance?.manual_transfers_total || 0)}
              {(balance?.bank_expense_total || 0) > 0 && (
                <span className="text-destructive"> − Banka giderleri {formatTL(balance.bank_expense_total)}</span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Toplam {items.length} manuel aktarım · {balance?.bank_expense_count || 0} banka gideri
            </div>
          </div>
          <Bank size={72} weight="fill" className="text-primary hidden md:block" />
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="transfers" data-testid="tab-transfers" className="rounded-sm uppercase tracking-widest text-xs">
            <ArrowRight size={14} className="mr-1" /> Banka Aktarımları
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses" className="rounded-sm uppercase tracking-widest text-xs">
            <Receipt size={14} className="mr-1" /> Giderler
          </TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-manual-income" className="rounded-sm uppercase tracking-widest text-xs">
            <Money size={14} className="mr-1" /> Manuel Gelir / Geçmiş Ciro
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transfers" className="mt-6 space-y-6">
      {/* PENDING */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-widest mb-3 text-muted-foreground">
          Elde Bekleyen (Aktarım Bekliyor)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cash */}
          <Card className="rounded-sm border-green-400/50 bg-green-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Money size={26} weight="fill" className="text-green-600" />
                  <div className="text-xs uppercase tracking-widest text-green-800 font-semibold">Nakit</div>
                </div>
                <div className="text-[10px] text-green-700 uppercase tracking-widest">{p.cash.count} işlem</div>
              </div>
              <div className="text-4xl font-bold font-mono text-green-700" data-testid="pending-cash">
                {formatTL(p.cash.amount)}
              </div>
              {isSuperAdmin && (
                <Button
                  disabled={p.cash.amount <= 0}
                  onClick={() => openDialog("cash")}
                  data-testid="transfer-cash-btn"
                  className="w-full mt-4 rounded-sm bg-green-600 hover:bg-green-700 text-white uppercase tracking-widest font-semibold text-xs">
                  <ArrowRight size={14} weight="bold" /> Bankaya Aktar
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Card */}
          <Card className="rounded-sm border-blue-400/50 bg-blue-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard size={26} weight="fill" className="text-blue-600" />
                  <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Kredi Kartı</div>
                </div>
                <div className="text-[10px] text-blue-700 uppercase tracking-widest">{p.card.count} çekim</div>
              </div>
              <div className="text-4xl font-bold font-mono text-blue-700" data-testid="pending-card">
                {formatTL(p.card.amount)}
              </div>
              {isSuperAdmin && (
                <Button
                  disabled={p.card.amount <= 0}
                  onClick={() => openDialog("card")}
                  data-testid="transfer-card-btn"
                  className="w-full mt-4 rounded-sm bg-blue-600 hover:bg-blue-700 text-white uppercase tracking-widest font-semibold text-xs">
                  <ArrowRight size={14} weight="bold" /> Bankaya Aktar
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Total / all */}
          <Card className="rounded-sm border-primary/60 bg-primary/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wallet size={26} weight="fill" className="text-primary" />
                  <div className="text-xs uppercase tracking-widest font-semibold">Toplam</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest">
                  {p.cash.count + p.card.count} işlem
                </div>
              </div>
              <div className="text-4xl font-bold font-mono" data-testid="pending-total">
                {formatTL(p.total)}
              </div>
              {isSuperAdmin && (
                <Button
                  disabled={p.total <= 0}
                  onClick={() => openDialog("all")}
                  data-testid="transfer-all-btn"
                  className="w-full mt-4 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold text-xs">
                  <ArrowRight size={14} weight="bold" /> Tümünü Aktar
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
        {!isSuperAdmin && (
          <div className="mt-3 text-[11px] text-muted-foreground italic">
            Aktarım işlemi yalnızca süperadmin tarafından yapılabilir.
          </div>
        )}
      </div>

      {/* HISTORY */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="p-5 border-b border-border">
            <h3 className="text-sm font-semibold uppercase tracking-widest">Aktarım Geçmişi</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead>Otopark</TableHead>
                <TableHead>Abonelik</TableHead>
                <TableHead>Aktaran</TableHead>
                <TableHead>Not</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Henüz aktarım kaydı yok
                  </TableCell>
                </TableRow>
              )}
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`bank-transfer-row-${t.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{formatDateTime(t.created_at)}</TableCell>
                  <TableCell>
                    {t.method === "cash" ? (
                      <Badge className="rounded-sm bg-green-100 text-green-800 border-green-300 uppercase text-[10px] tracking-widest">Nakit</Badge>
                    ) : (
                      <Badge className="rounded-sm bg-blue-100 text-blue-800 border-blue-300 uppercase text-[10px] tracking-widest">Kart</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatTL(t.amount)}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {formatTL(t.parking_amount)} <span className="opacity-60">({t.session_ids_count})</span>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {formatTL(t.subscription_amount)} <span className="opacity-60">({t.subscription_payment_ids_count})</span>
                  </TableCell>
                  <TableCell className="text-xs">{t.transferred_by_email || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.note || "—"}</TableCell>
                  <TableCell className="text-right">
                    {isSuperAdmin && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => revert(t.id)}
                        data-testid={`revert-transfer-${t.id}`}
                        title="Geri Al"
                        className="text-destructive hover:text-destructive">
                        <Trash size={14} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
          <ExpensesTab isSuperAdmin={isSuperAdmin} />
        </TabsContent>

        <TabsContent value="income" className="mt-6">
          <ManualIncomeTab isSuperAdmin={isSuperAdmin} />
        </TabsContent>
      </Tabs>

      {/* CONFIRM DIALOG */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bankaya Aktarım Onayı</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-sm border border-primary/30 bg-primary/5">
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Elde Bekleyen</div>
              <div className="text-2xl font-mono font-bold mt-1">
                {dialog.method === "cash" && formatTL(p.cash.amount) + " · Nakit"}
                {dialog.method === "card" && formatTL(p.card.amount) + " · Kart"}
                {dialog.method === "all" && formatTL(p.total) + " · Nakit + Kart"}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {dialog.method === "all"
                  ? "Tüm elde bekleyen ödemeler aktarılacak."
                  : (dialog.amount === "" || Number(dialog.amount) === (dialog.method === "cash" ? p.cash.amount : p.card.amount))
                    ? "Tüm elde bekleyen ödemeler aktarılacak."
                    : `Kısmi aktarım: ${formatTL(Number(dialog.amount) || 0)} bankaya, kalan ${formatTL((dialog.method === "cash" ? p.cash.amount : p.card.amount) - (Number(dialog.amount) || 0))} kasada.`}
              </div>
            </div>
            {dialog.method !== "all" && (
              <div>
                <Label>Aktarılacak Tutar (opsiyonel — boş bırakırsanız tümü)</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max={dialog.method === "cash" ? p.cash.amount : p.card.amount}
                    value={dialog.amount}
                    onChange={(e) => setDialog({ ...dialog, amount: e.target.value })}
                    className="rounded-sm font-mono"
                    placeholder={`Örn: ${dialog.method === "cash" ? p.cash.amount : p.card.amount}`}
                    data-testid="transfer-amount-input" />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm text-xs whitespace-nowrap"
                    onClick={() => setDialog({ ...dialog, amount: String(dialog.method === "cash" ? p.cash.amount : p.card.amount) })}
                    data-testid="transfer-amount-max-btn">
                    Tümü
                  </Button>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Örnek: nakit 17.000₺ var, bankaya 15.000₺ aktardıysanız buraya 15000 yazın. Kalan 2.000₺ kasada bekler.
                </div>
              </div>
            )}
            <div>
              <Label>Not (opsiyonel)</Label>
              <Input
                value={dialog.note}
                onChange={(e) => setDialog({ ...dialog, note: e.target.value })}
                className="rounded-sm"
                placeholder="Örn: 05.02.2026 - Banka X yatırıldı"
                data-testid="transfer-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog({ open: false, method: "all", note: "", amount: "" })}>
              İptal
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="transfer-confirm-btn"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              {submitting ? "Aktarılıyor..." : "Aktar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -----------------------------------------------------------------------------
// GİDERLER TAB — SGK/Maaş/Kira/Elektrik... süperadmin/admin ekler, süperadmin siler.
// Seçilen ödeme yöntemine göre ilgili buket (nakit/kart/banka) otomatik düşer.
// -----------------------------------------------------------------------------
function ExpensesTab({ isSuperAdmin }) {
  const [form, setForm] = useState({
    category: "SGK", amount: "", method: "bank_transfer", description: "",
    expense_date: new Date().toISOString().slice(0, 10),  // YYYY-MM-DD (bugün)
  });
  const [saving, setSaving] = useState(false);

  // Filter state
  const todayISO = new Date().toISOString().slice(0, 10);
  const monthStartISO = todayISO.slice(0, 7) + "-01";
  const [filters, setFilters] = useState({
    category: "all",
    method: "all",
    start: "",
    end: "",
    q_text: "",
  });
  const hasActiveFilter = (
    filters.category !== "all" || filters.method !== "all" ||
    filters.start || filters.end || filters.q_text
  );

  const buildQuery = () => {
    const p = new URLSearchParams({ limit: "500" });
    if (filters.category !== "all") p.set("category", filters.category);
    if (filters.method !== "all") p.set("method", filters.method);
    if (filters.start) p.set("start", filters.start);
    if (filters.end) p.set("end", filters.end);
    if (filters.q_text.trim()) p.set("q_text", filters.q_text.trim());
    return p.toString();
  };

  const { data: categories } = useLiveQuery(
    "expense-cats",
    async () => (await api.get("/bank-transfers/expense-categories")).data,
    { intervalMs: 300000 },
  );
  const { data: expensesRes } = useLiveQuery(
    [TAGS.BANK_TRANSFERS, "expenses-filter", buildQuery()],
    async () => (await api.get(`/bank-transfers/expenses?${buildQuery()}`)).data,
    { intervalMs: 15000, deps: [filters.category, filters.method, filters.start, filters.end, filters.q_text] },
  );
  const { data: summary } = useLiveQuery(
    TAGS.BANK_TRANSFERS,
    async () => (await api.get("/bank-transfers/expenses/summary")).data,
    { intervalMs: 15000 },
  );

  const cats = categories?.categories || [];
  const list = expensesRes?.items || [];
  const filteredTotal = expensesRes?.filtered_total || 0;
  const filteredCount = expensesRes?.filtered_count || 0;
  const filteredByMethod = expensesRes?.filtered_by_method || {};
  const sum = summary || { total: 0, total_count: 0, by_method: {
    cash: { amount: 0, count: 0 }, card: { amount: 0, count: 0 },
    bank_transfer: { amount: 0, count: 0 } } };

  const clearFilters = () => setFilters({
    category: "all", method: "all", start: "", end: "", q_text: "",
  });
  const applyThisMonth = () => setFilters({
    ...filters, start: monthStartISO, end: todayISO,
  });
  const applyToday = () => setFilters({
    ...filters, start: todayISO, end: todayISO,
  });

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0)
      return toast.error("Geçerli tutar girin");
    if (!form.expense_date)
      return toast.error("Gider tarihi seçin");
    // Tarihi UTC ISO ile gönder — gün ortasında (12:00) local time olarak,
    // TR gün sınırı hesaplamalarında sınırda kalmasın (UTC-3 kayma toleransı).
    const expenseIso = new Date(`${form.expense_date}T12:00:00`).toISOString();
    setSaving(true);
    try {
      await api.post("/bank-transfers/expenses", {
        category: form.category,
        amount: parseFloat(form.amount),
        method: form.method,
        description: form.description || null,
        expense_date: expenseIso,
      });
      const methodLabel = form.method === "cash" ? "Nakit" : form.method === "card" ? "Kart" : "Banka";
      toast.success(`Gider kaydedildi (${form.expense_date}) — ${methodLabel} bakiyesinden ${formatTL(parseFloat(form.amount))} düşüldü`);
      setForm({ ...form, amount: "", description: "" });
      invalidate([TAGS.BANK_TRANSFERS, TAGS.KASA]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu gider kaydı silinsin mi? İlgili buketten düşen tutar geri iade edilecek."))
      return;
    try {
      await api.delete(`/bank-transfers/expenses/${id}`);
      toast.success("Gider silindi");
      invalidate([TAGS.BANK_TRANSFERS, TAGS.KASA]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const methodLabel = (m) => ({
    cash: "Nakit", card: "Kart", bank_transfer: "Banka",
  }[m] || m);
  const methodBadge = (m) => {
    const cls = m === "cash"
      ? "bg-green-100 text-green-800 border-green-300"
      : m === "card"
      ? "bg-blue-100 text-blue-800 border-blue-300"
      : "bg-primary/10 text-primary border-primary/30";
    return (
      <Badge className={`rounded-sm ${cls} uppercase text-[10px] tracking-widest`}>
        {methodLabel(m)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-sm border-destructive/30 bg-destructive/5">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-destructive font-semibold">Toplam Gider</div>
            <div className="text-3xl font-bold font-mono text-destructive mt-2" data-testid="expenses-total">
              -{formatTL(sum.total)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">{sum.total_count} kayıt</div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-green-300 bg-green-50">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-green-800 font-semibold flex items-center gap-1">
              <Money size={12} /> Nakit Gider
            </div>
            <div className="text-2xl font-bold font-mono text-green-700 mt-2">{formatTL(sum.by_method.cash.amount)}</div>
            <div className="text-[10px] text-muted-foreground">{sum.by_method.cash.count} kayıt</div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-blue-300 bg-blue-50">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold flex items-center gap-1">
              <CreditCard size={12} /> Kart Gider
            </div>
            <div className="text-2xl font-bold font-mono text-blue-700 mt-2">{formatTL(sum.by_method.card.amount)}</div>
            <div className="text-[10px] text-muted-foreground">{sum.by_method.card.count} kayıt</div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-primary/40 bg-primary/5">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold flex items-center gap-1">
              <Bank size={12} /> Banka Gider
            </div>
            <div className="text-2xl font-bold font-mono text-primary mt-2">{formatTL(sum.by_method.bank_transfer.amount)}</div>
            <div className="text-[10px] text-muted-foreground">{sum.by_method.bank_transfer.count} kayıt</div>
          </CardContent>
        </Card>
      </div>

      {/* NEW EXPENSE FORM */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Plus size={18} weight="bold" className="text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">Yeni Gider</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tutar (₺)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="expense-amount-input"
                placeholder="0.00" />
            </div>
            <div>
              <Label>Gider Tarihi</Label>
              <Input
                type="date"
                value={form.expense_date}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                className="rounded-sm"
                data-testid="expense-date-input" />
            </div>
            <div>
              <Label>Ödeme Yöntemi</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="expense-method-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="bank_transfer">Banka Hesabı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-6">
              <Label>Açıklama (opsiyonel)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-sm"
                data-testid="expense-description-input"
                placeholder="Örn: Şubat 2026 elektrik faturası" />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="expense-save-btn"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold text-xs">
              {saving ? "Kaydediliyor..." : "Gider Ekle"}
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground mt-3 italic">
            💡 Gider seçilen ödeme yöntemine göre kasadaki ilgili buketten (nakit/kart/banka) otomatik düşer.
          </div>
        </CardContent>
      </Card>

      {/* FILTERS */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelSimple size={16} weight="bold" className="text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">Filtreler</h3>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={applyToday}
                    data-testid="expense-filter-today"
                    className="rounded-sm uppercase text-[10px] tracking-widest">
              Bugün
            </Button>
            <Button size="sm" variant="ghost" onClick={applyThisMonth}
                    data-testid="expense-filter-this-month"
                    className="rounded-sm uppercase text-[10px] tracking-widest">
              Bu Ay
            </Button>
            {hasActiveFilter && (
              <Button size="sm" variant="ghost" onClick={clearFilters}
                      data-testid="expense-filter-clear"
                      className="rounded-sm uppercase text-[10px] tracking-widest text-destructive">
                <X size={12} /> Temizle
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label>Kategori</Label>
              <Select value={filters.category}
                      onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="rounded-sm" data-testid="expense-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Yöntem</Label>
              <Select value={filters.method}
                      onValueChange={(v) => setFilters({ ...filters, method: v })}>
                <SelectTrigger className="rounded-sm" data-testid="expense-filter-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="bank_transfer">Banka</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Başlangıç</Label>
              <Input
                type="date"
                value={filters.start}
                onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                className="rounded-sm"
                data-testid="expense-filter-start" />
            </div>
            <div>
              <Label>Bitiş</Label>
              <Input
                type="date"
                value={filters.end}
                onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                className="rounded-sm"
                data-testid="expense-filter-end" />
            </div>
            <div>
              <Label>Açıklama Ara</Label>
              <Input
                value={filters.q_text}
                onChange={(e) => setFilters({ ...filters, q_text: e.target.value })}
                className="rounded-sm"
                data-testid="expense-filter-text"
                placeholder="örn: elektrik" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FILTERED TOTAL (yalnızca aktif filtre varsa) */}
      {hasActiveFilter && (
        <Card className="rounded-sm border-amber-300 bg-amber-50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-900 font-semibold flex items-center gap-1">
                  <FunnelSimple size={12} weight="bold" /> Filtreli Toplam
                </div>
                <div className="text-3xl font-bold font-mono text-amber-800 mt-1" data-testid="expense-filtered-total">
                  -{formatTL(filteredTotal)}
                </div>
                <div className="text-[11px] text-amber-800/70 mt-1">
                  {filteredCount} kayıt · seçilen filtrelere göre
                </div>
              </div>
              <div className="flex gap-2">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-green-800/70">Nakit</div>
                  <div className="font-mono font-bold text-green-700">{formatTL(filteredByMethod.cash?.amount || 0)}</div>
                  <div className="text-[9px] text-muted-foreground">{filteredByMethod.cash?.count || 0} kayıt</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-blue-800/70">Kart</div>
                  <div className="font-mono font-bold text-blue-700">{formatTL(filteredByMethod.card?.amount || 0)}</div>
                  <div className="text-[9px] text-muted-foreground">{filteredByMethod.card?.count || 0} kayıt</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-primary/70">Banka</div>
                  <div className="font-mono font-bold text-primary">{formatTL(filteredByMethod.bank_transfer?.amount || 0)}</div>
                  <div className="text-[9px] text-muted-foreground">{filteredByMethod.bank_transfer?.count || 0} kayıt</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* EXPENSES LIST */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-widest">
              Gider Geçmişi {hasActiveFilter && <span className="text-primary">(Filtrelendi)</span>}
            </h3>
            <div className="text-xs text-muted-foreground">{list.length} kayıt</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Kaydeden</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Henüz gider kaydı yok</TableCell></TableRow>
              )}
              {list.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{formatDateTime(e.expense_date || e.created_at)}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-widest">{e.category}</Badge></TableCell>
                  <TableCell>{methodBadge(e.method)}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-destructive">-{formatTL(e.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.description || "—"}</TableCell>
                  <TableCell className="text-xs">{e.created_by_email || "—"}</TableCell>
                  <TableCell className="text-right">
                    {isSuperAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => remove(e.id)}
                        data-testid={`expense-delete-${e.id}`} title="Sil"
                        className="text-destructive hover:text-destructive">
                        <Trash size={14} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// ============================================================================
// MANUEL GELİR / GEÇMİŞ CİRO — Iter 19
// Araç oturumu oluşturmadan, doğrudan gelir kaydı ekler. Kasa/Panel/Raporlar
// otomatik senkron olur (backend `bust_financial()` cache invalidasyonu).
// ============================================================================
function ManualIncomeTab({ isSuperAdmin }) {
  const [form, setForm] = useState({
    amount: "", income_type: "parking", method: "cash",
    income_date: new Date().toISOString().slice(0, 10),
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const todayISO = new Date().toISOString().slice(0, 10);
  const monthStartISO = todayISO.slice(0, 7) + "-01";
  const [filters, setFilters] = useState({
    income_type: "all", method: "all", start: "", end: "",
  });
  const hasActiveFilter = (
    filters.income_type !== "all" || filters.method !== "all" ||
    filters.start || filters.end
  );

  const buildQuery = () => {
    const p = new URLSearchParams({ limit: "500" });
    if (filters.income_type !== "all") p.set("income_type", filters.income_type);
    if (filters.method !== "all") p.set("method", filters.method);
    if (filters.start) p.set("start", filters.start);
    if (filters.end) p.set("end", filters.end);
    return p.toString();
  };

  const { data: incomesRes, refetch } = useLiveQuery(
    ["manual_incomes", buildQuery()],
    async () => (await api.get(`/manual-incomes?${buildQuery()}`)).data,
    { intervalMs: 15000, deps: [filters.income_type, filters.method, filters.start, filters.end] },
  );

  const list = incomesRes?.items || [];
  const total = incomesRes?.total || 0;
  const count = incomesRes?.count || 0;
  const byType = incomesRes?.by_type || { parking: 0, subscription: 0 };
  const byMethod = incomesRes?.by_method || {};

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Geçerli tutar girin");
    if (!form.income_date) return toast.error("Tarih seçin");
    const iso = new Date(`${form.income_date}T12:00:00`).toISOString();
    setSaving(true);
    try {
      await api.post("/manual-incomes", {
        amount: amt,
        income_date: iso,
        income_type: form.income_type,
        method: form.method,
        description: form.description || null,
      });
      const typeLabel = form.income_type === "parking" ? "Otopark" : "Abonelik";
      const methodLabel = form.method === "cash" ? "Nakit" : form.method === "card" ? "Kart" : "Banka";
      toast.success(`Manuel gelir kaydedildi — ${typeLabel} · ${methodLabel} · ${formatTL(amt)}`);
      setForm({ ...form, amount: "", description: "" });
      invalidate([TAGS.BANK_TRANSFERS, TAGS.KASA]);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const deleteIncome = async (mi) => {
    if (!window.confirm(
      `Bu manuel gelir kaydını silmek istediğinize emin misiniz?\n\n` +
      `Tutar: ${formatTL(mi.amount)}\nTip: ${mi.income_type === "parking" ? "Otopark" : "Abonelik"}\n` +
      `Yöntem: ${mi.method}\n\n⚠️ Kasa/raporlar otomatik güncellenir.`
    )) return;
    try {
      await api.delete(`/manual-incomes/${mi.id}`);
      toast.success("Manuel gelir silindi");
      invalidate([TAGS.BANK_TRANSFERS, TAGS.KASA]);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="space-y-6">
      {/* Toplam kartı */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="rounded-sm border-emerald-300 bg-emerald-50">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-emerald-800 font-semibold">Toplam Manuel Gelir</div>
            <div className="text-3xl font-mono font-bold text-emerald-700" data-testid="mi-total">{formatTL(total)}</div>
            <div className="text-[11px] text-emerald-800/70">{count} kayıt</div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Otopark</div>
            <div className="text-2xl font-mono font-bold text-primary">{formatTL(byType.parking || 0)}</div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Abonelik</div>
            <div className="text-2xl font-mono font-bold text-primary">{formatTL(byType.subscription || 0)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Form */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Money size={18} weight="fill" className="text-emerald-600" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">Yeni Manuel Gelir / Geçmiş Ciro</h3>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
            ℹ️ Bu kayıt <strong>araç oturumu oluşturmaz</strong>. Sadece gelir toplamına eklenir.
            Örn: geçmiş dönemin fiziksel kasadan alınan aidatı, cezasız kapatılan bir bakım geliri, vb.
            Ödeme yöntemine göre <strong>nakit/kart</strong> pending bucket&apos;ına, <strong>banka</strong> ise direkt Banka Hesabına düşer.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
              <Label>Tutar (₺)</Label>
              <Input type="number" step="0.01" min="0" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     className="rounded-sm font-mono" data-testid="mi-amount"
                     placeholder="0.00" />
            </div>
            <div>
              <Label>Tarih</Label>
              <Input type="date" value={form.income_date}
                     max={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setForm({ ...form, income_date: e.target.value })}
                     className="rounded-sm" data-testid="mi-date" />
            </div>
            <div>
              <Label>Gelir Tipi</Label>
              <Select value={form.income_type} onValueChange={(v) => setForm({ ...form, income_type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="mi-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parking">Otopark</SelectItem>
                  <SelectItem value="subscription">Abonelik</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ödeme Yöntemi</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="rounded-sm" data-testid="mi-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="bank_transfer">Banka Hesabı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Açıklama (opsiyonel)</Label>
              <Input value={form.description}
                     onChange={(e) => setForm({ ...form, description: e.target.value })}
                     className="rounded-sm" data-testid="mi-description"
                     placeholder="Örn: Şubat 2026 fiziksel kasa aidat toplamı" />
            </div>
          </div>
          <Button onClick={submit} disabled={saving} data-testid="mi-submit"
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus size={16} /> {saving ? "Kaydediliyor..." : "Kayıt Ekle"}
          </Button>
        </CardContent>
      </Card>

      {/* Filtreler */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelSimple size={16} weight="bold" className="text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">Filtreler</h3>
            <div className="flex-1" />
            <Button size="sm" variant="ghost"
                    onClick={() => setFilters({ ...filters, start: todayISO, end: todayISO })}
                    data-testid="mi-filter-today"
                    className="rounded-sm uppercase text-[10px] tracking-widest">Bugün</Button>
            <Button size="sm" variant="ghost"
                    onClick={() => setFilters({ ...filters, start: monthStartISO, end: todayISO })}
                    data-testid="mi-filter-this-month"
                    className="rounded-sm uppercase text-[10px] tracking-widest">Bu Ay</Button>
            {hasActiveFilter && (
              <Button size="sm" variant="ghost"
                      onClick={() => setFilters({ income_type: "all", method: "all", start: "", end: "" })}
                      data-testid="mi-filter-clear"
                      className="rounded-sm uppercase text-[10px] tracking-widest text-destructive">
                <X size={12} /> Temizle
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Tip</Label>
              <Select value={filters.income_type}
                      onValueChange={(v) => setFilters({ ...filters, income_type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="mi-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="parking">Otopark</SelectItem>
                  <SelectItem value="subscription">Abonelik</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Yöntem</Label>
              <Select value={filters.method}
                      onValueChange={(v) => setFilters({ ...filters, method: v })}>
                <SelectTrigger className="rounded-sm" data-testid="mi-filter-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="bank_transfer">Banka</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Başlangıç</Label>
              <Input type="date" value={filters.start}
                     onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                     className="rounded-sm" data-testid="mi-filter-start" />
            </div>
            <div>
              <Label>Bitiş</Label>
              <Input type="date" value={filters.end}
                     onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                     className="rounded-sm" data-testid="mi-filter-end" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtreli toplam kartı */}
      {hasActiveFilter && (
        <Card className="rounded-sm border-amber-300 bg-amber-50">
          <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-900 font-semibold flex items-center gap-1">
                <FunnelSimple size={12} weight="bold" /> Filtreli Toplam
              </div>
              <div className="text-3xl font-bold font-mono text-amber-800 mt-1" data-testid="mi-filtered-total">
                {formatTL(total)}
              </div>
              <div className="text-[11px] text-amber-800/70 mt-1">{count} kayıt</div>
            </div>
            <div className="flex gap-2">
              <div className="text-right"><div className="text-[10px] uppercase tracking-widest text-green-800/70">Nakit</div><div className="font-mono font-bold text-green-700">{formatTL(byMethod.cash?.amount || 0)}</div></div>
              <div className="text-right"><div className="text-[10px] uppercase tracking-widest text-blue-800/70">Kart</div><div className="font-mono font-bold text-blue-700">{formatTL(byMethod.card?.amount || 0)}</div></div>
              <div className="text-right"><div className="text-[10px] uppercase tracking-widest text-primary/70">Banka</div><div className="font-mono font-bold text-primary">{formatTL(byMethod.bank_transfer?.amount || 0)}</div></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-widest">
              Manuel Gelir Geçmişi {hasActiveFilter && <span className="text-primary">(Filtrelendi)</span>}
            </h3>
            <div className="text-xs text-muted-foreground">{list.length} kayıt</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Gelir Tarihi</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Kaydeden</TableHead>
                <TableHead>Aktarım</TableHead>
                {isSuperAdmin && <TableHead className="w-16 text-right">Sil</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow><TableCell colSpan={isSuperAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">Kayıt yok</TableCell></TableRow>
              )}
              {list.map((mi) => (
                <TableRow key={mi.id} data-testid={`mi-row-${mi.id}`}>
                  <TableCell className="text-xs">{new Date(mi.income_date).toLocaleDateString("tr-TR")}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-sm text-[10px] uppercase tracking-widest ${mi.income_type === "subscription" ? "border-purple-300 text-purple-700 bg-purple-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
                      {mi.income_type === "subscription" ? "Abonelik" : "Otopark"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono font-bold">{formatTL(mi.amount)}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{mi.method === "cash" ? "Nakit" : mi.method === "card" ? "Kart" : "Banka"}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{mi.description || "—"}</TableCell>
                  <TableCell className="text-xs">{mi.created_by_email}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {mi.transferred_to_bank_at ? (
                      <span className="text-emerald-700">✓ Bankada</span>
                    ) : (
                      mi.method === "bank_transfer" ? "—" : "Bekliyor"
                    )}
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => deleteIncome(mi)}
                              data-testid={`mi-delete-${mi.id}`}
                              disabled={!!mi.transferred_to_bank_at}
                              title={mi.transferred_to_bank_at ? "Önce aktarımı geri alın" : "Süperadmin: Kaydı sil"}
                              className="h-8 w-8 p-0 text-destructive disabled:opacity-30">
                        <Trash size={14} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
